import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Button,
  LinearProgress,
  CircularProgress,
  Snackbar,
  Alert,
  TextField,
  Drawer,
  Chip,
  Stack,
  Tooltip,
  Avatar,
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon, 
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon,
  Timer as TimerIcon,
  Error as ErrorIcon,
  ListAlt as ListAltIcon,
  Home as HomeIcon,
  MenuBook as MenuBookIcon,
  InfoOutlined as InfoOutlinedIcon,
  Person as PersonIcon,
  GridView as GridViewIcon,
  Close as CloseIcon,
  Replay as ReplayIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import testService from '../../services/testService';
import { QuestionImage } from '../../components/common/QuestionImage';
import MathRenderer from '../../components/common/MathRenderer';

/** Sentinel value stored when a student flags "I don't know" for a question. */
export const IDK_SENTINEL = '__IDK__';

/** Normalize options from API (array or object) to array of { id, text } for display. */
function stripOptionPrefix(value, position) {
  const text = String(value ?? '');
  const pattern = new RegExp(`^\\s*${position}\\s*[.)-]\\s*`);
  return text.replace(pattern, '').trim() || text;
}

function normalizeOptions(options) {
  if (!options) return [];
  if (Array.isArray(options)) {
    return options.map((opt, i) => {
      if (typeof opt === 'string') return { id: i + 1, text: stripOptionPrefix(opt, i + 1) };
      const id = opt.id ?? opt.option_id ?? i + 1;
      const rawText = opt.text ?? opt.option_text ?? opt.answer_text ?? `Option ${i + 1}`;
      return { id, text: stripOptionPrefix(rawText, i + 1) };
    });
  }
  if (typeof options === 'object' && options !== null) {
    return Object.entries(options).map(([key, val], i) => ({
      id: key,
      text: typeof val === 'string' ? val : String(val)
    }));
  }
  return [];
}

/** Whether question type is multiple choice (MCQ). */
function isMultipleChoice(type) {
  const t = (type || '').toUpperCase();
  return t === 'MULTIPLE' || t === 'MCQ' || t === 'MULTIPLE_CHOICE';
}

/** Whether question type is true/false. */
function isTrueFalse(type) {
  return (type || '').toUpperCase() === 'TRUE_FALSE';
}

/** Sort questions by id ascending to match PDF and grading order. */
function sortQuestionsByPdfOrder(questionsList) {
  if (!Array.isArray(questionsList) || questionsList.length <= 1) return questionsList;
  return [...questionsList].sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
}

const TestTaking = () => {
  const location = useLocation();
  const { testId } = useParams();
  const locationState = location.state || {};
  const initAttemptRef = useRef(false);
  const hasAutoSubmittedRef = useRef(false);
  const navigate = useNavigate();

  const mode = location.state?.mode || new URLSearchParams(location.search).get('mode') || 'EXAM';
  const targetQuestionIndex = location.state?.questionIndex;
  const effectiveAttemptId = locationState.attemptId;
  const forceFresh = location.state?.force_fresh || false;

  const [test, setTest] = useState(location.state?.test || null);
  const [attempt, setAttempt] = useState(null);
  // Only initialize questions from state if we explicitly have them (review scenario)
  // For normal test starts, start with empty array
  const [questions, setQuestions] = useState(
    location.state?.questions && location.state.questions.length > 0 
      ? location.state.questions 
      : []
  );
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(targetQuestionIndex || 0);
  const [answers, setAnswers] = useState(location.state?.answers || {});
  const [confidence, setConfidence] = useState(location.state?.confidence || {});
  const confidenceInitializedRef = useRef(false);
  const [selectedOptions, setSelectedOptions] = useState(location.state?.answers || {});
  const [subAnswers, setSubAnswers] = useState(location.state?.subAnswers || {}); // { questionId: { subQuestionId: answer } }
  const [saveTimeout, setSaveTimeout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [visitedQuestions, setVisitedQuestions] = useState(() => new Set([targetQuestionIndex || 0]));
  const [markedForReview, setMarkedForReview] = useState(() => new Set());
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Mark confidence as initialized if it comes from navigation state
  useEffect(() => {
    if (location.state?.confidence && Object.keys(location.state.confidence).length > 0) {
      confidenceInitializedRef.current = true;
    }
  }, [location.state?.confidence]);

  useEffect(() => {
    setVisitedQuestions(prev => new Set([...prev, currentQuestionIndex]));
  }, [currentQuestionIndex]);

  // Clear snackbar on mount if we have state data (review scenario)
  useEffect(() => {
    const hasStateData = (location.state?.questions && location.state.questions.length > 0) || 
                        (location.state?.answers && Object.keys(location.state.answers).length > 0);
    if (hasStateData) {
      // Clear any snackbar messages when we have state data
      setSnackbar({ open: false, message: '', severity: 'info' });
    }
  }, []); // Run once on mount

  const handleSubmitTest = useCallback(async () => {
    if (!attempt || submitting) return;
  
    if (attempt.status === 'SUBMITTED' || attempt.submitted || attempt.submitted_at) {
      setSnackbar({
        open: true,
        message: 'Test is already submitted',
        severity: 'info',
        autoHideDuration: 6000,
        redirect: true,
      });
      navigate(`/test/${testId}/results/${attempt.id}`, { replace: true });
      return;
    }
  
    setSubmitting(true);
    try {
      // Persist all sub-question answers before submit (in case debounce/blur did not fire)
      for (const q of questions) {
        if (q?.has_subquestions && q?.sub_questions?.length > 0 && attempt?.id && q?.id) {
          const subAnswersForQ = subAnswers[q.id] || {};
          if (Object.keys(subAnswersForQ).length > 0) {
            try {
              await testService.saveAnswer(attempt.id, q.id, '', Object.entries(subAnswersForQ).map(([subQId, ans]) => ({
                sub_question_id: parseInt(subQId, 10),
                answer: ans || ''
              })));
            } catch (e) {
              // Log but continue; submit will still use already-saved data
            }
          }
        }
      }

      const formattedAnswers = Object.entries(answers).map(([questionId, answer]) => {
        const question = questions.find(q => q.id === parseInt(questionId));
        const isTextAnswer = question?.question_type === 'text' ||
          (typeof answer === 'string' && isNaN(Number(answer)));
        const conf = confidence[questionId];
        const validConfidence = ['YES', 'SOMEWHAT', 'NO'].includes(conf) ? conf : 'SOMEWHAT';
        return {
          question_id: parseInt(questionId),
          answer: isTextAnswer ? answer : answer.toString(),
          confidence: validConfidence
        };
      });
  
      const submission = { answers: formattedAnswers };
      if (attempt.teacher_managed_student_id) {
        await testService.completeManagedAttempt(attempt.id);
      } else {
        await testService.submitTestAttempt(attempt.id, submission);
      }
      navigate(`/test/${testId}/results/${attempt.id}`, { replace: true });
    } catch (err) {
      const errorMessage = err.response?.data?.detail ||
        err.response?.data?.error?.[0] ||
        'Failed to submit test. Please try again.';
  
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
        autoHideDuration: 10000
      });
    } finally {
      setSubmitting(false);
      setSubmitDialogOpen(false);
    }
  }, [attempt, submitting, answers, questions, subAnswers, confidence, navigate, testId]);

  // Fetch test data and questions
  useEffect(() => {
    const abortController = new AbortController();
    const signal = abortController.signal;

    const fetchTestData = async () => {
      if (initAttemptRef.current) return;
      initAttemptRef.current = true;

      try {
        setLoading(true);
        setError(null);

        // Clear any existing snackbar messages
        setSnackbar({ open: false, message: '', severity: 'info' });

        let testData = null;

        // Try to get test data, but if it fails (e.g., for students), we'll get it from the attempt
        try {
          testData = await testService.getTestById(testId, { signal });
          setTest(testData);
        } catch (error) {
          // For students, we might not have direct access to test details
          // We'll get the test details from the attempt data instead
          // Don't log 404 errors as they're expected for students
          if (error.response?.status !== 404) {
            console.log('Could not fetch test data directly, will try from attempt:', error);
          }
        }
    
        let currentAttempt = null;
        
        // Check if we have state data for review (questions, answers, etc.)
        // Check both location.state and the initialized questions state
        const hasStateData = (location.state?.questions && location.state.questions.length > 0) || 
                             (questions.length > 0);
        const hasStateAnswers = (location.state?.answers && Object.keys(location.state.answers).length > 0) ||
                                (Object.keys(answers).length > 0);
        const hasAnyStateData = hasStateData || hasStateAnswers;
        
        console.log('State data check:', { 
          hasLocationState: !!location.state?.questions, 
          locationStateLength: location.state?.questions?.length,
          hasQuestionsState: questions.length > 0,
          hasStateAnswers: hasStateAnswers,
          hasAnyStateData,
          locationState: location.state
        });
    
        if (effectiveAttemptId) {
          // User is resuming or reviewing
          // If we have state data (review scenario), try to use it first
          if (hasAnyStateData) {
            // For review scenario, try to fetch attempt silently
            // If it fails, we can still proceed with state data without showing error
            try {
              currentAttempt = await testService.getTestAttempt(effectiveAttemptId, { signal });
              console.log('Successfully fetched attempt for review:', currentAttempt);
              
              // If we don't have test data, try to get it from the attempt
              if (!testData && currentAttempt.test) {
                testData = currentAttempt.test;
                setTest(testData);
              }
            } catch (error) {
              console.log('Could not fetch attempt, but have state data - proceeding silently with review');
              // For review, we can proceed with state data even if attempt fetch fails
              // Create a minimal attempt object from state - don't show error since we have all data
              currentAttempt = {
                id: effectiveAttemptId,
                status: 'SUBMITTED', // Assume submitted if we're reviewing
                test: location.state?.test || testData,
                mode: location.state?.attemptMode || 'EXAM',
                start_time: location.state?.startTime,
                test_duration: location.state?.durationMinutes
              };
              // Explicitly clear any snackbar - we have all necessary data from state
              setSnackbar({ open: false, message: '', severity: 'info' });
            }
          } else {
            // No state data - this is a resume scenario, we need the attempt
            try {
              console.log('Attempting to resume attempt:', effectiveAttemptId);
              currentAttempt = await testService.getTestAttempt(effectiveAttemptId, { signal });
              console.log('Successfully resumed attempt:', currentAttempt);
            
              // Check if attempt is already submitted
              if (currentAttempt.status === 'SUBMITTED' || currentAttempt.submitted || currentAttempt.submitted_at) {
                console.log('Attempt already submitted, redirecting to results');
                navigate(`/test/${testId}/results/${currentAttempt.id}`, { replace: true });
                return;
              }
              
              // If we don't have test data, try to get it from the attempt
              if (!testData && currentAttempt.test) {
                testData = currentAttempt.test;
                setTest(testData);
              }
            } catch (error) {
              if (error?.code === 'ERR_CANCELED' || error?.originalError?.code === 'ERR_CANCELED') throw error;
              console.error('Error resuming attempt:', error);
              // If we get a 404, the attemptId might be wrong (could be test ID instead)
              // Try to find the in-progress attempt for this test
              if (error.response?.status === 404) {
                console.log('Attempt not found (404), searching for in-progress attempt for test:', testId);
                try {
                  const existingAttempts = await testService.getTestAttempts(testId, { signal });
                  const inProgressAttempt = existingAttempts.find(attempt => 
                    attempt.status === 'IN_PROGRESS' && !attempt.submitted
                  );
                  if (inProgressAttempt) {
                    console.log('Found in-progress attempt:', inProgressAttempt.id);
                    currentAttempt = await testService.getTestAttempt(inProgressAttempt.id, { signal });
                    if (!testData && currentAttempt.test) {
                      testData = currentAttempt.test;
                      setTest(testData);
                    }
                  } else {
                    // No in-progress attempt found, set to null to start fresh
                    currentAttempt = null;
                  }
                } catch (searchError) {
                  console.error('Error searching for in-progress attempt:', searchError);
                  currentAttempt = null;
                }
              } else {
                // Only show error if we don't have state data to fall back on
                // Double-check for state data before showing error
                const hasStateDataNow = (location.state?.questions && location.state.questions.length > 0) || 
                                        (questions.length > 0) ||
                                        (location.state?.answers && Object.keys(location.state.answers).length > 0) ||
                                        (Object.keys(answers).length > 0);
                console.log('Error resuming - checking state data:', { hasStateDataNow, locationState: !!location.state });
                if (!hasStateDataNow) {
                  setSnackbar({
                    open: true,
                    message: 'Failed to resume test. The attempt may no longer exist. Starting fresh...',
                    severity: 'warning',
                    autoHideDuration: 5000
                  });
                } else {
                  console.log('Have state data, not showing error message');
                }
                // If we can't fetch the attempt, try to start fresh
                currentAttempt = null;
              }
            }
          }
        }
        
        if (!currentAttempt) {
          // User is starting fresh - first check if there's already a submitted attempt
          try {
            const existingAttempts = await testService.getTestAttempts(testId, { signal });
            
            // Check if there's a submitted attempt
            const submittedAttempt = existingAttempts.find(attempt => 
              attempt.status === 'SUBMITTED' || attempt.submitted || attempt.submitted_at
            );
            
            if (submittedAttempt) {
              navigate(`/test/${testId}/results/${submittedAttempt.id}`, { replace: true });
              return;
            }
            
            // Check if there's an in-progress attempt
            const inProgressAttempt = existingAttempts.find(attempt => 
              attempt.status === 'IN_PROGRESS' && !attempt.submitted
            );
            
            if (inProgressAttempt) {
              currentAttempt = await testService.getTestAttempt(inProgressAttempt.id, { signal });
              
              // If we don't have test data, try to get it from the attempt
              if (!testData && currentAttempt.test) {
                testData = currentAttempt.test;
                setTest(testData);
              }
            } else {
              // Double-check that no submitted attempts exist before starting
              try {
                const finalCheck = await testService.getTestAttempts(testId, { signal });
                const submittedAttempt = finalCheck.find(attempt => 
                  attempt.status === 'SUBMITTED' || attempt.submitted || attempt.submitted_at
                );
                if (submittedAttempt) {
                  navigate(`/test/${testId}/results/${submittedAttempt.id}`, { replace: true });
                  return;
                }
              } catch (error) {
                // Continue with creating new attempt
              }
              
              currentAttempt = await testService.startTestAttempt(testId, mode, forceFresh, { signal });
              
              // If we don't have test data, try to get it from the attempt
              if (!testData && currentAttempt.test) {
                testData = currentAttempt.test;
                setTest(testData);
              }
            }
          } catch (error) {
            const isAbort = error?.name === 'AbortError' || error?.code === 'ERR_CANCELED' ||
              error?.message?.toLowerCase?.().includes('canceled') || error?.originalError?.code === 'ERR_CANCELED';
            if (isAbort) throw error;
            try {
              currentAttempt = await testService.startTestAttempt(testId, mode, forceFresh, { signal });
              if (!testData && currentAttempt?.test) {
                testData = currentAttempt.test;
                setTest(testData);
              }
            } catch (startError) {
              const startIsAbort = startError?.code === 'ERR_CANCELED' || startError?.originalError?.code === 'ERR_CANCELED';
              if (startIsAbort) throw startError;
              throw new Error('Failed to start test attempt. Please try again.');
            }
          }
        }
        
        // Ensure we have a valid attempt
        if (!currentAttempt || !currentAttempt.id) {
          throw new Error('Failed to initialize test attempt. Please try again.');
        }
    
        setAttempt(currentAttempt);
    
        // Try to get questions, but if it fails, we'll get them from the attempt or state
        let questionsData = null;
        
        // First, check if we already have questions from state (e.g., when reviewing after completion)
        // Only use state questions if we explicitly have them from navigation state
        const hasStateQuestions = location.state?.questions && location.state.questions.length > 0;
        const hasAttemptQuestions = currentAttempt?.questions && Array.isArray(currentAttempt.questions) && currentAttempt.questions.length > 0;

        if (hasStateQuestions) {
          questionsData = sortQuestionsByPdfOrder(location.state.questions);
          setQuestions(questionsData);
          console.log('Using questions from navigation state:', questionsData.length);
        } else if (hasAttemptQuestions) {
          // Use questions from start/resume attempt response (same order as PDF)
          questionsData = sortQuestionsByPdfOrder(currentAttempt.questions);
          setQuestions(questionsData);
          console.log('Using questions from attempt response:', questionsData.length);
        } else if (currentAttempt?.answers?.length > 0) {
          // Extract from attempt answers; preserve backend order (question__id) then sort to match PDF
          const questionMap = new Map();
          currentAttempt.answers.forEach((answer) => {
            const qd = answer.question_details;
            if (qd?.id && !questionMap.has(qd.id)) {
              questionMap.set(qd.id, {
                id: qd.id,
                text: qd.text || qd.question_text,
                question_text: qd.text || qd.question_text,
                question_type: qd.type || qd.question_type,
                options: qd.options || [],
                correct_answer: qd.correct_answer,
                explanation: qd.explanation,
                marks: qd.marks || 1,
                image_url: qd.image_url || qd.image_proxy_url,
                image_proxy_url: qd.image_proxy_url,
                has_subquestions: qd.has_subquestions,
                sub_questions: qd.sub_questions || [],
                test_id: currentAttempt.test || currentAttempt.test_id
              });
            }
          });
          if (questionMap.size > 0) {
            questionsData = sortQuestionsByPdfOrder(Array.from(questionMap.values()));
            setQuestions(questionsData);
            console.log('Using questions from attempt answers:', questionsData.length);
          }
        }

        if (!questionsData) {
          try {
            questionsData = await testService.getTestQuestions(testId, { signal });
            if (!questionsData || !Array.isArray(questionsData) || questionsData.length === 0) {
              throw new Error('API returned empty or invalid questions');
            }
            questionsData = sortQuestionsByPdfOrder(questionsData);
            setQuestions(questionsData);
            console.log('Fetched questions from API:', questionsData.length);
          } catch (error) {
            if (error.response?.status !== 404) {
              console.error('Failed to fetch questions from API:', error);
            }
          }
          if (!questionsData && location.state?.questions?.length > 0) {
            questionsData = sortQuestionsByPdfOrder(location.state.questions);
            setQuestions(questionsData);
            console.log('Using questions from state fallback:', questionsData.length);
          }
          if (!questionsData) {
            console.error('All question loading methods failed:', {
              hasAttempt: !!currentAttempt,
              attemptQuestions: currentAttempt?.questions,
              attemptAnswers: currentAttempt?.answers,
              hasStateQuestions: !!location.state?.questions,
              testId
            });
            throw new Error('Failed to load test questions. Please try again.');
          }
        }
        
        // Load saved answers and sub-answers if resuming
        if (currentAttempt.answers) {
          const savedAnswers = {};
          const savedSubAnswers = {};
          currentAttempt.answers.forEach(a => {
            const questionId = a.question || a.question_id || (a.question_details && a.question_details.id);
            if (questionId) {
              savedAnswers[questionId] = a.answer || '';
              if (a.sub_answers && Array.isArray(a.sub_answers) && a.sub_answers.length > 0) {
                savedSubAnswers[questionId] = {};
                a.sub_answers.forEach(sa => {
                  const subQId = sa.sub_question ?? sa.sub_question_id;
                  if (subQId != null) savedSubAnswers[questionId][subQId] = sa.answer ?? '';
                });
              }
            }
          });
          // When coming from Review with state, preserve state answers/subAnswers and merge API data
          // Otherwise, use API data (for resume scenario)
          if (hasAnyStateData && (Object.keys(location.state?.answers || {}).length > 0 || Object.keys(location.state?.subAnswers || {}).length > 0)) {
            // Merge: state takes precedence, but add any new answers from API
            const mergedAnswers = { ...savedAnswers, ...(location.state?.answers || {}) };
            const mergedSubAnswers = { ...savedSubAnswers };
            // Merge subAnswers: preserve state, add any from API that aren't in state
            Object.keys(savedSubAnswers).forEach(qId => {
              if (!mergedSubAnswers[qId]) mergedSubAnswers[qId] = {};
              Object.assign(mergedSubAnswers[qId], savedSubAnswers[qId]);
            });
            if (location.state?.subAnswers) {
              Object.keys(location.state.subAnswers).forEach(qId => {
                if (!mergedSubAnswers[qId]) mergedSubAnswers[qId] = {};
                Object.assign(mergedSubAnswers[qId], location.state.subAnswers[qId]);
              });
            }
            setAnswers(mergedAnswers);
            setSelectedOptions(mergedAnswers);
            setSubAnswers(mergedSubAnswers);
          } else {
            // Normal resume: use API data
            setAnswers(savedAnswers);
            setSelectedOptions(savedAnswers);
            setSubAnswers(prev => ({ ...prev, ...savedSubAnswers }));
          }

          // Resume at first unanswered question when not opening a specific index (e.g. from review)
          if (targetQuestionIndex == null && questionsData && questionsData.length > 0) {
            let firstUnanswered = 0;
            for (let i = 0; i < questionsData.length; i++) {
              const q = questionsData[i];
              const hasSubs = q?.has_subquestions && q?.sub_questions?.length > 0;
              if (hasSubs) {
                const subAns = savedSubAnswers[q.id] || {};
                const allAnswered = q.sub_questions.every(sq => {
                  const v = subAns[sq.id];
                  return v != null && String(v).trim() !== '';
                });
                if (!allAnswered) { firstUnanswered = i; break; }
              } else {
                const a = savedAnswers[q.id];
                if (a === undefined || a === null || String(a).trim() === '') {
                  firstUnanswered = i;
                  break;
                }
              }
            }
            setCurrentQuestionIndex(firstUnanswered);
          }
        }
    
      } catch (err) {
        // Ignore aborted/canceled requests (e.g. from React Strict Mode or navigation)
        const isAbort = err?.name === 'AbortError' || err?.code === 'ERR_CANCELED' ||
          err?.message?.toLowerCase?.().includes('aborted') || err?.message?.toLowerCase?.().includes('canceled') ||
          err?.originalError?.code === 'ERR_CANCELED' || err?.originalError?.name === 'AbortError';
        if (isAbort) return;
        // Handle specific error about already submitted test
        if (err.message && err.message.includes('already submitted')) {
          try {
            const existingAttempts = await testService.getTestAttempts(testId, { signal });
            const submittedAttempt = existingAttempts.find(attempt => 
              attempt.status === 'SUBMITTED' || attempt.submitted || attempt.submitted_at
            );
            if (submittedAttempt) {
              navigate(`/test/${testId}/results/${submittedAttempt.id}`, { replace: true });
              return;
            }
          } catch (redirectError) {
            // Continue to error state
          }
          navigate('/tests', { replace: true });
          return;
        }
        
        // Check if we have state data - if so, don't show error since we can proceed
        // Only suppress error if we have BOTH questions and answers from state (review scenario)
        const hasStateData = location.state?.questions && location.state.questions.length > 0;
        const hasStateAnswers = location.state?.answers && Object.keys(location.state.answers).length > 0;
        const hasCompleteStateData = hasStateData && hasStateAnswers;
        
        if (hasCompleteStateData) {
          console.log('Error occurred but have complete state data - proceeding without error message');
          // Don't set error - we have all the data we need from state
          // Ensure questions are set from state if not already set
          if (questions.length === 0 && hasStateData) {
            setQuestions(location.state.questions);
          }
          setLoading(false);
          return;
        }
        
        // For normal test loading (not review), show the error
        setError(err.message || 'Failed to load test');
      } finally {
        setLoading(false);
      }
    };

    fetchTestData();

    return () => {
      abortController.abort();
      initAttemptRef.current = false;
    };
  }, [testId, effectiveAttemptId, mode, navigate, forceFresh]);
 
  // Timer effect
  useEffect(() => {
    if (!attempt || !test) return;

    const mode = attempt.mode;
    const start = attempt.start_time;
    const duration = test.duration_minutes || attempt.test_duration || attempt.test?.duration_minutes;

    if (mode === 'EXAM' && start && duration) {
      const endTime = new Date(start);
      endTime.setMinutes(endTime.getMinutes() + duration);

      // Declare timer variable before the update function
      let timer = null;

      const update = () => {
        const now = new Date();
        const diff = Math.max(0, endTime - now);
        const timeLeftSeconds = Math.ceil(diff / 1000);
        setTimeLeft(timeLeftSeconds);
        if (diff <= 0) {
          // Don't redirect when user came from review page to view a specific question
          const fromReviewToViewQuestion = location.state?.questionIndex !== undefined;
          if (!hasAutoSubmittedRef.current && !fromReviewToViewQuestion) {
            hasAutoSubmittedRef.current = true;
            // Navigate to review instead of auto-submitting - never submit with possibly blank answers
            navigate(`/tests/${attempt.id}/review`);
          }
          if (timer) {
            clearInterval(timer);
          }
        }
      };

      update();
      timer = setInterval(update, 1000);
      return () => {
        if (timer) {
          clearInterval(timer);
        }
      };
    } else {
      setTimeLeft(null);
    }
  }, [attempt, test, attempt?.start_time, attempt?.id, handleSubmitTest, location.state?.questionIndex]);
  
  // Restore answers and sub-answers from attempt (e.g. when attempt refetched or questions loaded after answers)
  // Only restore if we don't already have answers from state (e.g. when coming from Review)
  useEffect(() => {
    if (questions.length === 0 || !attempt?.answers?.length) return;
    
    // If we have state answers/subAnswers (from Review navigation), don't overwrite them
    const hasStateAnswers = location.state?.answers && Object.keys(location.state.answers).length > 0;
    const hasStateSubAnswers = location.state?.subAnswers && Object.keys(location.state.subAnswers).length > 0;
    if (hasStateAnswers || hasStateSubAnswers) {
      console.log('Skipping restore effect - have state answers/subAnswers from Review');
      return;
    }
    
    const restoredAnswers = {};
    const restoredSubAnswers = {};
    attempt.answers.forEach((a) => {
      const qid = a.question_details?.id ?? a.question ?? a.question_id;
      if (qid) {
        restoredAnswers[qid] = a.answer ?? '';
        if (a.sub_answers?.length) {
          restoredSubAnswers[qid] = {};
          a.sub_answers.forEach(sa => {
            const subQId = sa.sub_question ?? sa.sub_question_id;
            if (subQId != null) restoredSubAnswers[qid][subQId] = sa.answer ?? '';
          });
        }
      }
    });
    // Only update if we have restored data and current state is empty or different
    if (Object.keys(restoredAnswers).length > 0 || Object.keys(restoredSubAnswers).length > 0) {
      setAnswers(prev => {
        // Only update if prev is empty or if restored has new data
        if (Object.keys(prev).length === 0 || Object.keys(restoredAnswers).length > 0) {
          return { ...prev, ...restoredAnswers };
        }
        return prev;
      });
      setSelectedOptions(prev => {
        if (Object.keys(prev).length === 0 || Object.keys(restoredAnswers).length > 0) {
          return { ...prev, ...restoredAnswers };
        }
        return prev;
      });
      setSubAnswers(prev => {
        // Merge restored subAnswers, but don't overwrite existing non-empty values
        const merged = { ...prev };
        Object.keys(restoredSubAnswers).forEach(qId => {
          if (!merged[qId]) merged[qId] = {};
          // Only add restored sub-answers if they don't exist or are empty
          Object.keys(restoredSubAnswers[qId]).forEach(subQId => {
            if (!merged[qId][subQId] || merged[qId][subQId] === '') {
              merged[qId][subQId] = restoredSubAnswers[qId][subQId];
            }
          });
        });
        return merged;
      });
    }
  }, [questions.length, attempt?.answers, location.state?.answers, location.state?.subAnswers]);
  
  // Check if we have a valid attempt
  useEffect(() => {
    if (!loading && !attempt && !error) {
      const timeoutId = setTimeout(() => {
        if (!attempt) {
          navigate('/tests', { replace: true });
        }
      }, 2000);
      
      return () => clearTimeout(timeoutId);
    }
  }, [loading, attempt, error, navigate]);
  
  const handleOptionSelect = (questionId, value) => {
    if (loading || !attempt?.id || attempt.submitted || attempt.status === 'SUBMITTED' || attempt.submitted_at || attempt.end_time) {
      return;
    }
    
    const answerValue = typeof value === 'number' ? value.toString() : value;
    
    setAnswers(prev => ({
      ...prev,
      [questionId]: answerValue
    }));
    
    setSelectedOptions(prev => ({
      ...prev,
      [questionId]: answerValue
    }));
    
    saveAnswer(attempt.id, questionId, answerValue);
  };
  
  const handleSubAnswerSelect = (questionId, subQuestionId, answer) => {
    if (loading || !attempt?.id || attempt.submitted || attempt.status === 'SUBMITTED' || attempt.submitted_at || attempt.end_time) {
      return;
    }
    
    setSubAnswers(prev => ({
      ...prev,
      [questionId]: {
        ...(prev[questionId] || {}),
        [subQuestionId]: answer
      }
    }));
    
    // Save all sub-answers for this question together
    const currentSubAnswers = {
      ...(subAnswers[questionId] || {}),
      [subQuestionId]: answer
    };
    saveAnswerWithSubAnswers(attempt.id, questionId, '', currentSubAnswers);
  };
  
  const saveAnswerWithSubAnswers = async (attemptId, questionId, mainAnswer, subAnswersForQuestion) => {
    try {
      // Prepare sub_answers_data array
      const subAnswersData = Object.entries(subAnswersForQuestion).map(([subQId, answer]) => ({
        sub_question_id: parseInt(subQId),
        answer: answer || ''
      }));
      
      // Save main answer with sub-answers
      await testService.saveAnswer(attemptId, questionId, mainAnswer, subAnswersData);
    } catch (err) {
      const errorDetail = err.response?.data?.detail || '';
      const errorArray = err.response?.data?.error || [];
      const errorMessage = err.response?.data?.message || '';
      
      const isSubmittedError = 
        errorDetail.toLowerCase().includes('submitted') || 
        errorArray.some(e => e.toLowerCase().includes('submitted')) ||
        errorMessage.toLowerCase().includes('submitted');
      
      if (!isSubmittedError) {
        console.error('Error saving answer with sub-answers:', err);
      }
    }
  };
  
  const saveAnswer = async (attemptId, questionId, answer) => {
    try {
      await testService.saveAnswer(attemptId, questionId, answer);
    } catch (err) {
      const errorDetail = err.response?.data?.detail || '';
      const errorArray = err.response?.data?.error || [];
      const errorMessage = err.response?.data?.message || '';
      
      const isSubmittedError = 
        errorDetail.toLowerCase().includes('submitted') || 
        errorArray.some(e => e.toLowerCase().includes('submitted')) ||
        errorDetail.toLowerCase().includes('already submitted') ||
        errorDetail.toLowerCase().includes('cannot save answers for a submitted test attempt') ||
        errorMessage.toLowerCase().includes('submitted');
      
      if (isSubmittedError) {
        setSnackbar({
          open: true,
          message: 'Test is already submitted',
          severity: 'info',
          autoHideDuration: 3000
        });
        navigate(`/test/${testId}/results/${attemptId}`, { replace: true });
        return;
      }
      
      const errorMessageToShow = err.response?.data?.error?.[0] || 
                               err.response?.data?.detail || 
                               err.response?.data?.message ||
                               'Failed to save your answer. Please try again.';
      
      setSnackbar({
        open: true,
        message: errorMessageToShow,
        severity: 'warning',
        autoHideDuration: 3000
      });
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      const nextIndex = currentQuestionIndex - 1;
      setCurrentQuestionIndex(nextIndex);
      setVisitedQuestions(prev => new Set([...prev, nextIndex]));
    }
  };

  const goToReview = () => {
    if (!attempt?.id) {
      setSnackbar({
        open: true,
        message: 'No valid attempt found. Please try starting the test again.',
        severity: 'error',
        autoHideDuration: 5000
      });
      return;
    }
    navigate(`/tests/${attempt.id}/review`, {
      state: {
        answers,
        subAnswers,
        questions,
        testId,
        confidence,
        attemptId: attempt.id,
        attemptMode: attempt.mode,
        startTime: attempt.start_time,
        durationMinutes: test?.duration_minutes || attempt.test_duration
      }
    });
  };
  
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
    if (snackbar.redirect) {
      navigate(`/test/${testId}/results/${attempt?.id}`, { replace: true });
    }
  };

  /** Exit test and go to home (tests list), saving current question state so user can resume later. */
  const handleExit = async () => {
    if (attempt?.status === 'SUBMITTED' || attempt?.submitted || attempt?.submitted_at) {
      navigate('/tests', { replace: true });
      return;
    }
    const currentQ = questions[currentQuestionIndex];
    if (attempt?.id && currentQ?.id) {
      const hasSubs = currentQ?.has_subquestions && currentQ?.sub_questions?.length > 0;
      const answer = selectedOptions[currentQ.id];
      const subAnswersForQ = subAnswers[currentQ.id] || {};
      const hasAnswer = hasSubs ? (Object.keys(subAnswersForQ).length > 0) : (answer !== undefined && answer !== '');
      if (hasAnswer) {
        try {
          if (hasSubs) {
            await saveAnswerWithSubAnswers(attempt.id, currentQ.id, '', subAnswersForQ);
          } else {
            await saveAnswer(attempt.id, currentQ.id, answer);
          }
        } catch (e) {
          // still navigate; answer may have been saved on change
        }
      }
    }
    setSnackbar({ open: true, message: 'Progress saved. You can resume from the tests list.', severity: 'info', autoHideDuration: 4000 });
    navigate('/tests', { replace: true });
  };

  const handleNext = async () => {
    const currentQ = questions[currentQuestionIndex];
    const hasSubs = currentQ?.has_subquestions && currentQ?.sub_questions?.length > 0;
    const answer = selectedOptions[currentQ?.id];
    const subAnswersForQ = currentQ?.id ? (subAnswers[currentQ.id] || {}) : {};

    if (attempt?.status === 'SUBMITTED' || attempt?.submitted || attempt?.submitted_at) {
      setSnackbar({
        open: true,
        message: 'Test is already submitted',
        severity: 'info',
        autoHideDuration: 3000
      });
      navigate(`/test/${testId}/results/${attempt.id}`, { replace: true });
      return;
    }

    if (!attempt?.id || !currentQ?.id) {
      setSnackbar({
        open: true,
        message: 'Cannot save. Test is already submitted',
        severity: 'error',
        autoHideDuration: 5000
      });
      navigate('/tests', { replace: true });
      return;
    }
    const answered = hasSubs ? (Object.keys(subAnswersForQ).length > 0) : (answer !== undefined);
    if (!answered) return;

    try {
      if (hasSubs) {
        await saveAnswerWithSubAnswers(attempt.id, currentQ.id, '', subAnswersForQ);
      } else {
        await saveAnswer(attempt.id, currentQ.id, answer);
      }
      const nextIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(nextIndex);
      setVisitedQuestions(prev => new Set([...prev, nextIndex]));
    } catch (err) {
      if (err.response?.data?.detail?.includes('submitted') || 
          err.response?.data?.error?.some(e => e.includes('submitted')) ||
          err.response?.data?.detail?.includes('Cannot save answers for a submitted test attempt')) {
        setSnackbar({
          open: true,
          message: 'Test is already submitted',
          severity: 'info',
          autoHideDuration: 3000
        });
        navigate(`/test/${testId}/results/${attempt.id}`, { replace: true });
        return;
      }
      
      setSnackbar({
        open: true,
        message: 'Could not save your answer. Please check your connection.',
        severity: 'error',
        autoHideDuration: 5000
      });
    }
  };

  const handleQuestionJump = (index) => {
    setCurrentQuestionIndex(index);
    setVisitedQuestions(prev => new Set([...prev, index]));
    setPaletteOpen(false);
  };

  const handleClearResponse = async () => {
    const currentQ = questions[currentQuestionIndex];
    if (!currentQ?.id || !attempt?.id || attempt?.submitted || attempt?.status === 'SUBMITTED') return;

    setAnswers(prev => {
      const next = { ...prev };
      delete next[currentQ.id];
      return next;
    });
    setSelectedOptions(prev => {
      const next = { ...prev };
      delete next[currentQ.id];
      return next;
    });
    setSubAnswers(prev => {
      const next = { ...prev };
      delete next[currentQ.id];
      return next;
    });

    await saveAnswer(attempt.id, currentQ.id, '');
  };

  const handleMarkForReviewAndNext = async () => {
    const currentQ = questions[currentQuestionIndex];
    if (!currentQ?.id) return;

    setMarkedForReview(prev => new Set([...prev, currentQ.id]));

    const hasQuestionSubs = currentQ?.has_subquestions && currentQ?.sub_questions?.length > 0;
    const answer = selectedOptions[currentQ.id];
    const subAnswersForQ = subAnswers[currentQ.id] || {};
    const hasAnswer = hasQuestionSubs
      ? Object.values(subAnswersForQ).some(value => value != null && String(value).trim() !== '')
      : answer !== undefined && answer !== null && String(answer).trim() !== '';

    if (attempt?.id && hasAnswer && !attempt?.submitted && attempt?.status !== 'SUBMITTED') {
      if (hasQuestionSubs) {
        await saveAnswerWithSubAnswers(attempt.id, currentQ.id, '', subAnswersForQ);
      } else {
        await saveAnswer(attempt.id, currentQ.id, answer);
      }
    }

    if (!isLastQuestion) {
      const nextIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(nextIndex);
      setVisitedQuestions(prev => new Set([...prev, nextIndex]));
    } else {
      goToReview();
    }
  };

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress size={60} thickness={4} />
        <Box mt={3}>
          <Typography variant="h6" color="textSecondary">Loading test...</Typography>
        </Box>
      </Box>
    );
  }
  
  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <ErrorIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h5" color="error" gutterBottom>
            Error Loading Test
          </Typography>
          <Typography variant="body1" paragraph>
            {error}
          </Typography>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />} 
            onClick={() => navigate(-1)}
            sx={{ mt: 2 }}
          >
            Go Back
          </Button>
        </Paper>
      </Container>
    );
  }

  const currentQuestion = questions && questions.length > 0 && currentQuestionIndex < questions.length 
    ? questions[currentQuestionIndex] 
    : null;

  const displayTitle = test?.title || test?.name || test?.test_title || test?.testName || 'Test';
  const progress = questions && questions.length > 0 ? ((currentQuestionIndex + 1) / questions.length) * 100 : 0;
  const isLastQuestion = questions && currentQuestionIndex === questions.length - 1;
  // Mark main question as answered when all sub-questions are answered (so Next/Review enable)
  const hasSubs = currentQuestion?.has_subquestions && currentQuestion?.sub_questions?.length > 0;
  const allSubsAnswered = hasSubs && currentQuestion.sub_questions.every(
    (sq) => { const v = subAnswers[currentQuestion.id]?.[sq.id]; return v != null && String(v).trim() !== ''; }
  );
  const isCurrentQuestionAnswered = currentQuestion
    ? (selectedOptions[currentQuestion.id] === IDK_SENTINEL || (hasSubs ? allSubsAnswered : !!selectedOptions[currentQuestion.id]))
    : false;

  const formatTime = (seconds) => {
    if (seconds === null) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const isQuestionAnswered = (question) => {
    if (!question?.id) return false;
    // IDK flag counts as answered
    if (selectedOptions[question.id] === IDK_SENTINEL) return true;
    if (question.has_subquestions && question.sub_questions?.length > 0) {
      const answersForQuestion = subAnswers[question.id] || {};
      return question.sub_questions.every((subQuestion) => {
        const value = answersForQuestion[subQuestion.id];
        return value != null && String(value).trim() !== '';
      });
    }
    const value = selectedOptions[question.id];
    return value != null && String(value).trim() !== '';
  };

  const getQuestionStatus = (question, index) => {
    if (!question) return 'notVisited';
    const answered = isQuestionAnswered(question);
    const marked = markedForReview.has(question.id);
    if (answered && marked) return 'answeredMarked';
    if (marked) return 'marked';
    if (answered) return 'answered';
    if (visitedQuestions.has(index)) return 'notAnswered';
    return 'notVisited';
  };

  const statusStyles = {
    answered: { label: 'Answered', bg: '#2e7d32', border: '#1b5e20', color: '#fff' },
    notAnswered: { label: 'Not Answered', bg: '#ef6c00', border: '#e65100', color: '#fff' },
    notVisited: { label: 'Not Visited', bg: '#f5f7fb', border: '#cfd8dc', color: '#263238' },
    marked: { label: 'Marked for Review', bg: '#6a3dbb', border: '#4a2388', color: '#fff' },
    answeredMarked: { label: 'Answered & Marked for Review', bg: '#6a3dbb', border: '#2e7d32', color: '#fff' },
  };

  const questionTypeLabel = currentQuestion?.has_subquestions
    ? 'Composite'
    : (currentQuestion?.question_type || currentQuestion?.type || 'Question').toString().replace(/_/g, ' ');
  const marksLabel = currentQuestion?.marks != null ? `${currentQuestion.marks} mark${Number(currentQuestion.marks) === 1 ? '' : 's'}` : 'Marks N/A';
  const subjectLabel = test?.subject_name || test?.subject?.name || test?.subject || currentQuestion?.subject_name || 'Assessment';
  const studentName = attempt?.student_name || attempt?.student?.name || attempt?.user_name || 'Student';
  const paletteCounts = questions.reduce((acc, question, index) => {
    const status = getQuestionStatus(question, index);
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  const renderQuestionPalette = () => (
    <Box sx={{ width: { xs: 320, sm: 360, lg: '100%' }, maxWidth: '100%', p: { xs: 2, lg: 0 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={800}>Question Palette</Typography>
          <Typography variant="caption" color="text.secondary">
            {questions.length} questions
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={<ListAltIcon />}
          onClick={goToReview}
          disabled={submitting || !attempt?.id}
        >
          Review
        </Button>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 1, mb: 2.5 }}>
        {questions.map((question, index) => {
          const status = getQuestionStatus(question, index);
          const styles = statusStyles[status];
          const active = index === currentQuestionIndex;
          return (
            <Tooltip key={question.id || index} title={`${styles.label}: Question ${index + 1}`}>
              <Button
                aria-label={`Go to question ${index + 1}, ${styles.label}`}
                onClick={() => handleQuestionJump(index)}
                sx={{
                  minWidth: 0,
                  height: 42,
                  borderRadius: active ? '10px' : '6px',
                  bgcolor: styles.bg,
                  color: styles.color,
                  border: `2px solid ${active ? '#1a237e' : styles.border}`,
                  fontWeight: 800,
                  boxShadow: active ? '0 0 0 3px rgba(63,81,181,0.18)' : 'none',
                  '&:hover': { bgcolor: styles.bg, filter: 'brightness(0.96)' },
                }}
              >
                {index + 1}
              </Button>
            </Tooltip>
          );
        })}
      </Box>

      <Box sx={{ display: 'grid', gap: 1 }}>
        {Object.entries(statusStyles).map(([key, styles]) => (
          <Box key={key} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                aria-hidden="true"
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: key === 'marked' || key === 'answeredMarked' ? '50%' : '4px',
                  bgcolor: styles.bg,
                  border: `2px solid ${styles.border}`,
                }}
              />
              <Typography variant="caption" color="text.secondary">{styles.label}</Typography>
            </Box>
            <Typography variant="caption" fontWeight={700}>{paletteCounts[key] || 0}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );

  return (
    <Container
      maxWidth={false}
      sx={{
        minHeight: '100vh',
        bgcolor: '#f4f7fb',
        py: { xs: 1.5, sm: 2 },
        px: { xs: 1, md: 2 },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          mb: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 1.5,
            p: { xs: 1.5, md: 2 },
            bgcolor: '#263238',
            color: '#fff',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" fontWeight={800} noWrap>
              {displayTitle}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.78)' }} noWrap>
              {subjectLabel}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip
              icon={<TimerIcon />}
              label={`Time Left: ${formatTime(timeLeft)}`}
              color={timeLeft !== null && timeLeft < 60 ? 'error' : 'default'}
              sx={{ bgcolor: '#fff', fontWeight: 800 }}
            />

            <Button
              size="small"
              variant="outlined"
              startIcon={<InfoOutlinedIcon />}
              onClick={() => navigate(`/test/${testId}/instructions`)}
              sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)' }}
            >
              Instructions
            </Button>

          </Stack>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ height: 6 }}
        />
      </Paper>

      {/* Nav bar — full width above the two-column layout */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1.5,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {mode !== 'EXAM' && (
            <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
              Back to Tests
            </Button>
          )}
          <Button
            size="small"
            startIcon={<HomeIcon />}
            onClick={handleExit}
            variant="outlined"
            color="primary"
            disabled={submitting}
          >
            Exit
          </Button>
          <Chip label={`Question ${currentQuestionIndex + 1} of ${questions?.length || 0}`} size="small" />
        </Stack>
        <Button
          sx={{ display: { xs: 'inline-flex', lg: 'none' } }}
          variant="outlined"
          size="small"
          startIcon={<GridViewIcon />}
          onClick={() => setPaletteOpen(true)}
        >
          Questions
        </Button>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Box sx={{ flex: 2, minWidth: 0 }}>

      {currentQuestion ? (
        <Paper elevation={1} sx={{ p: { xs: 2, sm: 3 }, mb: 2, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              <Chip label={`Q.No: ${currentQuestionIndex + 1}`} color="primary" size="small" />
              <Chip label={`Q.Type: ${questionTypeLabel}`} variant="outlined" size="small" />
              <Chip label={marksLabel} color="success" size="small" variant="outlined" />
            </Stack>
            <Box mb={2}>
              <MathRenderer content={currentQuestion.question_text || 'Question'} variant="h6" />
            </Box>
            {/* Same image rendering as teacher View Question */}
            {(currentQuestion.image_proxy_url || currentQuestion.image_url) && (
              <Box mb={3} textAlign="center">
                <QuestionImage question={currentQuestion} alt="question" />
              </Box>
            )}
          </Box>
          
          {/* Show sub-questions if question has sub-questions */}
          {currentQuestion.has_subquestions && currentQuestion.sub_questions && currentQuestion.sub_questions.length > 0 ? (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Answer the following questions:
              </Typography>
              {currentQuestion.sub_questions.map((subQ, subIndex) => {
                const currentSubAnswer = subAnswers[currentQuestion.id]?.[subQ.id] || '';
                
                return (
                  <Box key={subQ.id} sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Box sx={{ mb: 1.5, fontWeight: 600 }}>
                      <Typography variant="subtitle2" component="span" sx={{ fontWeight: 600 }}>{subQ.letter || String.fromCharCode(97 + subIndex)}. </Typography>
                      <MathRenderer content={subQ.question_text || ''} variant="subtitle2" sx={{ fontWeight: 600 }} />
                    </Box>
                    
                    {isMultipleChoice(subQ.question_type) && normalizeOptions(subQ.options).length > 0 ? (
                      <FormControl component="fieldset" fullWidth>
                        <RadioGroup
                          value={currentSubAnswer || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            setSubAnswers(prev => ({
                              ...prev,
                              [currentQuestion.id]: {
                                ...(prev[currentQuestion.id] || {}),
                                [subQ.id]: value
                              }
                            }));
                            if (attempt?.id && !attempt?.submitted && attempt?.status !== 'SUBMITTED') {
                              handleSubAnswerSelect(currentQuestion.id, subQ.id, value);
                            }
                          }}
                        >
                          {normalizeOptions(subQ.options).map((opt, optIndex) => {
                            const optionText = opt.text ?? String(opt.id);
                            const isSelected = (currentSubAnswer ?? '').toString().trim().toLowerCase() === optionText.toString().trim().toLowerCase();
                            return (
                              <Paper
                                key={opt.id ?? optIndex}
                                variant="outlined"
                                sx={{
                                  p: 1.5,
                                  mb: 1,
                                  borderColor: isSelected ? 'primary.main' : 'divider',
                                  borderWidth: isSelected ? 2 : 1,
                                  '&:hover': {
                                    borderColor: 'primary.main',
                                    cursor: 'pointer',
                                  },
                                }}
                                onClick={() => {
                                  setSubAnswers(prev => ({
                                    ...prev,
                                    [currentQuestion.id]: {
                                      ...(prev[currentQuestion.id] || {}),
                                      [subQ.id]: optionText
                                    }
                                  }));
                                  
                                  if (attempt?.id && !attempt?.submitted && attempt?.status !== 'SUBMITTED') {
                                    handleSubAnswerSelect(currentQuestion.id, subQ.id, optionText);
                                  }
                                }}
                              >
                                <FormControlLabel
                                  value={optionText}
                                  control={<Radio checked={isSelected} />}
                                  label={<MathRenderer content={optionText} variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }} />}
                                  sx={{
                                    width: '100%',
                                    m: 0,
                                    '& .MuiFormControlLabel-label': {
                                      whiteSpace: 'normal',
                                      wordBreak: 'break-word'
                                    }
                                  }}
                                />
                              </Paper>
                            );
                          })}
                        </RadioGroup>
                      </FormControl>
                    ) : isTrueFalse(subQ.question_type) ? (
                      <FormControl component="fieldset" fullWidth>
                        <RadioGroup
                          value={currentSubAnswer || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            setSubAnswers(prev => ({
                              ...prev,
                              [currentQuestion.id]: {
                                ...(prev[currentQuestion.id] || {}),
                                [subQ.id]: value
                              }
                            }));
                            
                            if (attempt?.id && !attempt?.submitted && attempt?.status !== 'SUBMITTED') {
                              handleSubAnswerSelect(currentQuestion.id, subQ.id, value);
                            }
                          }}
                        >
                          <FormControlLabel value="True" control={<Radio />} label="True" />
                          <FormControlLabel value="False" control={<Radio />} label="False" />
                        </RadioGroup>
                      </FormControl>
                    ) : (
                      <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Type your answer here..."
                        value={currentSubAnswer}
                        onChange={(e) => {
                          const value = e.target.value;
                          setSubAnswers(prev => ({
                            ...prev,
                            [currentQuestion.id]: {
                              ...(prev[currentQuestion.id] || {}),
                              [subQ.id]: value
                            }
                          }));
                          
                          if (value.trim() && attempt?.id) {
                            if (saveTimeout) {
                              clearTimeout(saveTimeout);
                            }
                            
                            const timeoutId = setTimeout(() => {
                              if (attempt?.id && !attempt?.submitted && attempt?.status !== 'SUBMITTED') {
                                handleSubAnswerSelect(currentQuestion.id, subQ.id, value);
                              }
                            }, 1000);
                            
                            setSaveTimeout(timeoutId);
                          }
                        }}
                        onBlur={(e) => {
                          const value = e.target.value ?? '';
                          if (attempt?.id && !attempt?.submitted && attempt?.status !== 'SUBMITTED') {
                            if (saveTimeout) {
                              clearTimeout(saveTimeout);
                              setSaveTimeout(null);
                            }
                            handleSubAnswerSelect(currentQuestion.id, subQ.id, value);
                          }
                        }}
                        multiline
                        rows={3}
                        sx={{ mt: 1 }}
                      />
                    )}
                  </Box>
                );
              })}
            </Box>
          ) : (
            /* Regular question without sub-questions: input by question_type */
            <Box flex={1}>
              {(() => {
                const qType = currentQuestion.question_type || currentQuestion.type || '';
                const opts = normalizeOptions(currentQuestion.options);
                const isMcq = isMultipleChoice(qType) && opts.length > 0;
                const isTf = isTrueFalse(qType);

                if (isMcq) {
                  return (
                    <FormControl component="fieldset" fullWidth>
                      <RadioGroup
                        value={selectedOptions[currentQuestion.id] ?? ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setAnswers(prev => ({ ...prev, [currentQuestion.id]: value }));
                          setSelectedOptions(prev => ({ ...prev, [currentQuestion.id]: value }));
                          if (attempt?.id && !attempt?.submitted && attempt?.status !== 'SUBMITTED') {
                            handleOptionSelect(currentQuestion.id, value);
                          }
                        }}
                      >
                        {opts.map((opt) => {
                          const optionText = opt.text ?? String(opt.id);
                          const selected = (selectedOptions[currentQuestion.id] ?? '').toString().trim().toLowerCase() === optionText.toString().trim().toLowerCase();
                          return (
                            <Paper
                              key={opt.id}
                              variant="outlined"
                              sx={{
                                p: 2,
                                mb: 1,
                                borderColor: selected ? 'primary.main' : 'divider',
                                borderWidth: selected ? 2 : 1,
                                '&:hover': { borderColor: 'primary.main', cursor: 'pointer' },
                              }}
                              onClick={() => {
                                setAnswers(prev => ({ ...prev, [currentQuestion.id]: optionText }));
                                setSelectedOptions(prev => ({ ...prev, [currentQuestion.id]: optionText }));
                                if (attempt?.id && !attempt?.submitted && attempt?.status !== 'SUBMITTED') {
                                  handleOptionSelect(currentQuestion.id, optionText);
                                }
                              }}
                            >
                              <FormControlLabel
                              value={optionText}
                              control={<Radio checked={selected} />}
                              label={<MathRenderer content={optionText} variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }} />}
                              sx={{ width: '100%', m: 0, '& .MuiFormControlLabel-label': { whiteSpace: 'normal', wordBreak: 'break-word' } }}
                            />
                            </Paper>
                          );
                        })}
                      </RadioGroup>
                    </FormControl>
                  );
                }
                if (isTf) {
                  return (
                    <FormControl component="fieldset" fullWidth>
                      <RadioGroup
                        value={selectedOptions[currentQuestion.id] ?? ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setAnswers(prev => ({ ...prev, [currentQuestion.id]: value }));
                          setSelectedOptions(prev => ({ ...prev, [currentQuestion.id]: value }));
                          if (attempt?.id && !attempt?.submitted && attempt?.status !== 'SUBMITTED') {
                            handleOptionSelect(currentQuestion.id, value);
                          }
                        }}
                      >
                        <FormControlLabel value="True" control={<Radio />} label="True" />
                        <FormControlLabel value="False" control={<Radio />} label="False" />
                      </RadioGroup>
                    </FormControl>
                  );
                }
                return (
                  <TextField
                      fullWidth
                      variant="outlined"
                      placeholder="Type your answer here..."
                      value={selectedOptions[currentQuestion.id] || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setAnswers(prev => ({ ...prev, [currentQuestion.id]: value }));
                        setSelectedOptions(prev => ({ ...prev, [currentQuestion.id]: value }));
                        if (value.trim() && attempt?.id) {
                          if (saveTimeout) {
                            clearTimeout(saveTimeout);
                          }
                          
                          const timeoutId = setTimeout(() => {
                            if (attempt?.id && !attempt?.submitted && attempt?.status !== 'SUBMITTED') {
                              handleOptionSelect(currentQuestion.id, value);
                            }
                          }, 1000);
                          
                          setSaveTimeout(timeoutId);
                        }
                      }}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value.trim() && attempt?.id && !attempt?.submitted && attempt?.status !== 'SUBMITTED') {
                          if (saveTimeout) {
                            clearTimeout(saveTimeout);
                            setSaveTimeout(null);
                          }
                          handleOptionSelect(currentQuestion.id, value);
                        }
                      }}
                      multiline
                      rows={4}
                      sx={{ mt: 2 }}
                    />
                );
              })()}
            </Box>
          )}

          {/* I don't know option — available for every question */}
          {currentQuestion && (
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Button
                variant={selectedOptions[currentQuestion.id] === IDK_SENTINEL ? 'contained' : 'outlined'}
                color={selectedOptions[currentQuestion.id] === IDK_SENTINEL ? 'warning' : 'inherit'}
                size="small"
                onClick={() => {
                  const isAlreadyIdk = selectedOptions[currentQuestion.id] === IDK_SENTINEL;
                  const newValue = isAlreadyIdk ? '' : IDK_SENTINEL;
                  setAnswers(prev => ({ ...prev, [currentQuestion.id]: newValue }));
                  setSelectedOptions(prev => ({ ...prev, [currentQuestion.id]: newValue }));
                  if (attempt?.id && !attempt?.submitted && attempt?.status !== 'SUBMITTED') {
                    handleOptionSelect(currentQuestion.id, newValue);
                  }
                }}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                {selectedOptions[currentQuestion.id] === IDK_SENTINEL ? "✓ I don't know (tap to undo)" : "I don't know the answer"}
              </Button>
              {selectedOptions[currentQuestion.id] === IDK_SENTINEL && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1.5 }}>
                  Flagged — you can still move on and submit.
                </Typography>
              )}
            </Box>
          )}
        </Paper>
      ) : (
        <Paper elevation={2} sx={{ p: 3, mb: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No question found
          </Typography>
        </Paper>
      )}
      
          <Paper
            elevation={1}
            sx={{
              position: 'sticky',
              bottom: 0,
              zIndex: 5,
              p: 1.5,
              mt: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              display: 'flex',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
              bgcolor: '#fff',
            }}
          >
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                onClick={handlePrevious}
                disabled={currentQuestionIndex === 0 || submitting}
                startIcon={<ArrowBackIcon />}
              >
                Previous
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                onClick={handleClearResponse}
                disabled={!isCurrentQuestionAnswered || submitting || !attempt?.id}
                startIcon={<ReplayIcon />}
              >
                Clear Response
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleMarkForReviewAndNext}
                disabled={submitting}
                startIcon={<ListAltIcon />}
              >
                Mark for Review & Next
              </Button>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="contained"
                color="primary"
                onClick={isLastQuestion ? goToReview : handleNext}
                disabled={!isCurrentQuestionAnswered || submitting || !attempt?.id}
                endIcon={isLastQuestion ? <CheckCircleIcon /> : <ArrowForwardIcon />}
              >
                {isLastQuestion ? 'Save & Review' : 'Save & Next'}
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={goToReview}
                disabled={submitting || !attempt?.id}
              >
                Submit Test
              </Button>
            </Stack>
          </Paper>
        </Box>

        <Paper
          elevation={1}
          sx={{
            display: { xs: 'none', lg: 'block' },
            flex: '0 0 33%',
            minWidth: 280,
            maxWidth: 480,
            p: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            position: 'sticky',
            top: 16,
            maxHeight: 'calc(100vh - 32px)',
            overflow: 'auto',
          }}
        >
          {renderQuestionPalette()}
        </Paper>
      </Box>

      <Drawer anchor="right" open={paletteOpen} onClose={() => setPaletteOpen(false)}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <Button startIcon={<CloseIcon />} onClick={() => setPaletteOpen(false)}>
            Close
          </Button>
        </Box>
        {renderQuestionPalette()}
      </Drawer>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.autoHideDuration || 6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default TestTaking;
